import { Tabs as TabsPrimitive } from '@base-ui/react/tabs'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

/* -------------------------------------------------------------------------- */
/* Tabs root                                                                  */
/* -------------------------------------------------------------------------- */

function Tabs({ className, orientation = 'horizontal', ...props }: TabsPrimitive.Root.Props) {
  return (
    <TabsPrimitive.Root
      className={cn(
        // Layout: horizontal stacks list above content; vertical is a row.
        'group/tabs flex gap-2 data-[orientation=horizontal]:flex-col',
        className
      )}
      data-orientation={orientation}
      data-slot="tabs"
      {...props}
    />
  )
}

/* -------------------------------------------------------------------------- */
/* TabsList — base (all variants)                                             */
/* -------------------------------------------------------------------------- */

const tabsListBase = cn(
  'group/tabs-list inline-flex w-fit items-center justify-center',
  'rounded-lg p-[3px] text-muted-foreground',
  // Line variant removes list rounding (underline rail).
  'data-[variant=line]:rounded-none',
  // Pill variant: loose buttons, no segmented rail.
  'data-[variant=pill]:rounded-none data-[variant=pill]:bg-transparent data-[variant=pill]:p-0',
  // Follow Tabs root orientation.
  'group-data-horizontal/tabs:h-10 group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col'
)

/* -------------------------------------------------------------------------- */
/* TabsList — cva variants                                                      */
/* -------------------------------------------------------------------------- */

const tabsListVariants = cva(tabsListBase, {
  variants: {
    variant: {
      /** Filled muted pill rail (default shadcn-style). */
      default: 'bg-muted ',

      /** Transparent rail; active tab uses bottom underline (see TabsTrigger `after:`). */
      line: 'gap-1 bg-transparent ',

      /**
       * Folder-style row: baseline border under inactives; active tab has L/T/R border only
       * and overlaps the bottom line.
       */
      tabs: cn(
        'h-auto min-h-10 gap-1 rounded-none border-b border-border bg-transparent p-0 shadow-none',
        'group-data-horizontal/tabs:h-auto group-data-horizontal/tabs:min-h-10 group-data-horizontal/tabs:items-end group-data-horizontal/tabs:justify-start'
      ),

      /** Separate default-style buttons with fully rounded (pill) edges. */
      pill: 'justify-start gap-2 bg-transparent p-0 shadow-none',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

function TabsList({
  className,
  variant = 'default',
  ...props
}: TabsPrimitive.List.Props & VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      className={cn(tabsListVariants({ variant }), className)}
      data-slot="tabs-list"
      data-variant={variant}
      {...props}
    />
  )
}

/* -------------------------------------------------------------------------- */
/* TabsTrigger — shared (all list variants)                                   */
/* -------------------------------------------------------------------------- */

const tabsTriggerLayout = cn(
  'relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5',
  'whitespace-nowrap rounded-md border border-transparent px-1.5 py-0.5'
)

const tabsTriggerTypography = cn(
  'font-medium text-foreground/60 text-base transition-all hover:text-foreground'
)

const tabsTriggerFocus = cn(
  'focus-visible:border-ring focus-visible:outline-1 focus-visible:outline-ring',
  'focus-visible:ring-[3px] focus-visible:ring-ring/50'
)

const tabsTriggerDisabled = cn(
  'disabled:pointer-events-none disabled:opacity-50',
  'aria-disabled:pointer-events-none aria-disabled:opacity-50'
)

const tabsTriggerIconGutter = cn(
  'has-data-[icon=inline-end]:pr-1 has-data-[icon=inline-start]:pl-1'
)

const tabsTriggerVerticalTabsRoot = cn(
  'group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start'
)

/** Default + line lists: shadow when segmented / none when line or pill buttons. */
const tabsTriggerListVariantShadows = cn(
  'group-data-[variant=default]/tabs-list:data-active:shadow-sm',
  'group-data-[variant=line]/tabs-list:data-active:shadow-none',
  'group-data-[variant=pill]/tabs-list:data-active:shadow-none'
)

const tabsTriggerDarkBase = cn(
  'dark:text-muted-foreground dark:hover:text-primary-foreground/80'
)

const tabsTriggerSvg = cn(
  '[&_svg:not([class*=size-])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0'
)

/* -------------------------------------------------------------------------- */
/* TabsTrigger — line list variant                                            */
/* -------------------------------------------------------------------------- */

const tabsTriggerLineVariant = cn(
  'group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-active:bg-transparent',
  'dark:group-data-[variant=line]/tabs-list:data-active:border-transparent dark:group-data-[variant=line]/tabs-list:data-active:bg-transparent'
)

/* -------------------------------------------------------------------------- */
/* TabsTrigger — folder / “tabs” list variant (inactive)                      */
/* -------------------------------------------------------------------------- */

const tabsTriggerTabsVariantInactive = cn(
  // `transition-all` on typography animates borders / -mb-px / bg on active toggle — flashes a bright seam at the rail.
  'group-data-[variant=tabs]/tabs-list:transition-none',
  'group-data-[variant=tabs]/tabs-list:h-auto group-data-[variant=tabs]/tabs-list:flex-none',
  'group-data-[variant=tabs]/tabs-list:rounded-none group-data-[variant=tabs]/tabs-list:border-0 group-data-[variant=tabs]/tabs-list:bg-transparent',
  'group-data-[variant=tabs]/tabs-list:shadow-none group-data-[variant=tabs]/tabs-list:after:hidden',
  'group-data-[variant=tabs]/tabs-list:px-3 group-data-[variant=tabs]/tabs-list:py-2'
)

/* -------------------------------------------------------------------------- */
/* TabsTrigger — folder / “tabs” list variant (active)                        */
/* -------------------------------------------------------------------------- */

const tabsTriggerTabsVariantActiveChrome = cn(
  'group-data-[variant=tabs]/tabs-list:data-active:z-10 group-data-[variant=tabs]/tabs-list:data-active:-mb-px',
  'group-data-[variant=tabs]/tabs-list:data-active:rounded-none group-data-[variant=tabs]/tabs-list:data-active:rounded-t-md',
  'group-data-[variant=tabs]/tabs-list:data-active:border-l group-data-[variant=tabs]/tabs-list:data-active:border-t group-data-[variant=tabs]/tabs-list:data-active:border-r',
  'group-data-[variant=tabs]/tabs-list:data-active:border-border group-data-[variant=tabs]/tabs-list:data-active:[border-bottom:none]',
  'group-data-[variant=tabs]/tabs-list:data-active:bg-background group-data-[variant=tabs]/tabs-list:data-active:shadow-none'
)

/* -------------------------------------------------------------------------- */
/* TabsTrigger — data-active (cross-variant) + dark active                    */
/* -------------------------------------------------------------------------- */

const tabsTriggerActiveState = cn(
  'data-active:bg-background data-active:text-foreground',
  'group-data-[variant=tabs]/tabs-list:data-active:bg-primary',
  'dark:group-data-[variant=default]/tabs-list:data-active:border-input',
  'dark:data-active:bg-primary/80 dark:data-active:text-primary-foreground',
  'dark:data-active:font-semibold'
)

const tabsTriggerTabsVariantActiveColors = cn(
  'group-data-[variant=tabs]/tabs-list:data-active:text-primary-foreground',
  'dark:group-data-[variant=tabs]/tabs-list:data-active:border-border',
  'dark:group-data-[variant=tabs]/tabs-list:data-active:[border-bottom:none]',
  'dark:group-data-[variant=tabs]/tabs-list:data-active:bg-primary/80'
)

/* -------------------------------------------------------------------------- */
/* TabsTrigger — line variant bottom underline (`after:`)                     */
/* -------------------------------------------------------------------------- */

const tabsTriggerLineUnderline = cn(
  'after:absolute after:bg-primary after:opacity-0 after:transition-opacity',
  'group-data-horizontal/tabs:after:inset-x-0 group-data-horizontal/tabs:after:bottom-[-5px] group-data-horizontal/tabs:after:h-0.5',
  'group-data-vertical/tabs:after:inset-y-0 group-data-vertical/tabs:after:-right-1 group-data-vertical/tabs:after:w-0.5',
  'group-data-[variant=line]/tabs-list:data-active:after:opacity-100'
)

/** Pill list: outline-style inactives; active matches default Button (primary). */
const tabsTriggerPillVariant = cn(
  'group-data-[variant=pill]/tabs-list:flex-none group-data-[variant=pill]/tabs-list:h-9 group-data-[variant=pill]/tabs-list:rounded-full group-data-[variant=pill]/tabs-list:px-3',
  'group-data-[variant=pill]/tabs-list:border-border group-data-[variant=pill]/tabs-list:bg-background group-data-[variant=pill]/tabs-list:text-muted-foreground',
  // Inactive hover: accent (light) / input (dark) reads clearly vs background — matches outline Button affordance.
  'group-data-[variant=pill]/tabs-list:aria-[selected=false]:hover:bg-accent group-data-[variant=pill]/tabs-list:aria-[selected=false]:hover:text-accent-foreground',
  'dark:group-data-[variant=pill]/tabs-list:aria-[selected=false]:hover:bg-input/50 dark:group-data-[variant=pill]/tabs-list:aria-[selected=false]:hover:text-foreground',
  'dark:group-data-[variant=pill]/tabs-list:border-input dark:group-data-[variant=pill]/tabs-list:bg-input/30',
  'group-data-[variant=pill]/tabs-list:data-active:border-transparent group-data-[variant=pill]/tabs-list:data-active:bg-primary',
  'group-data-[variant=pill]/tabs-list:data-active:text-primary-foreground group-data-[variant=pill]/tabs-list:data-active:hover:bg-primary/80',
  'group-data-[variant=pill]/tabs-list:data-active:hover:text-primary-foreground',
  'dark:group-data-[variant=pill]/tabs-list:data-active:bg-primary dark:group-data-[variant=pill]/tabs-list:data-active:text-primary-foreground',
  'dark:group-data-[variant=pill]/tabs-list:data-active:hover:bg-primary/80',
  // Lift on hover (all enabled tabs, including selected).
  'group-data-[variant=pill]/tabs-list:enabled:hover:-translate-y-[2px] group-data-[variant=pill]/tabs-list:enabled:hover:z-10',
  'group-data-[variant=pill]/tabs-list:after:hidden'
)

function TabsTrigger({ className, ...props }: TabsPrimitive.Tab.Props) {
  return (
    <TabsPrimitive.Tab
      className={cn(
        // Shared
        tabsTriggerLayout,
        tabsTriggerTypography,
        tabsTriggerFocus,
        tabsTriggerDisabled,
        tabsTriggerIconGutter,
        tabsTriggerVerticalTabsRoot,
        tabsTriggerListVariantShadows,
        tabsTriggerDarkBase,
        tabsTriggerSvg,
        // Line list
        tabsTriggerLineVariant,
        // Folder “tabs” list
        tabsTriggerTabsVariantInactive,
        tabsTriggerTabsVariantActiveChrome,
        // Active + dark
        tabsTriggerActiveState,
        tabsTriggerTabsVariantActiveColors,
        // Line underline
        tabsTriggerLineUnderline,
        // Pill list (default-style button chrome)
        tabsTriggerPillVariant,
        className
      )}
      data-slot="tabs-trigger"
      {...props}
    />
  )
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      className={cn('flex-1 text-base outline-none', className)}
      data-slot="tabs-content"
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants }
